import { Component, OnDestroy, OnInit } from '@angular/core';
import * as showdown from 'showdown';
import { finalize } from 'rxjs/operators';

import { CodeGemRequest, CodeGemService } from '../../services/code-gem.service';
import { showdownMarkdownClassesExtension } from '../../shared/markdown/showdown-md-classes.extension';

interface Lesson {
  id: string;
  title: string;
  intro: string;
  logic: string;
  code: string;
  problem: string;
  task: string;
  tips: string[];
  demoScript: string[];
  dataset?: string;
}

interface LevelGroup {
  id: string;
  title: string;
  subtitle: string;
  lessons: Lesson[];
}

interface CourseCategory {
  id: string;
  title: string;
  description: string;
  levels: LevelGroup[];
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

@Component({
  selector: 'app-dynamic-code',
  templateUrl: './dynamic-code.component.html',
  styleUrls: ['./dynamic-code.component.scss']
})
export class DynamicCodeComponent implements OnInit, OnDestroy {
  categories: CourseCategory[] = [
    {
      id: 'python-for-ai',
      title: 'Python for AI',
      description: 'Seven hands-on days to tighten Python fundamentals, APIs, and data pipelines for AI workflows.',
      levels: [
        {
          id: 'python-day-1',
          title: 'Day 1 — Python fundamentals that affect AI code',
          subtitle: 'Data shapes, containers, and the payload reshaping muscle.',
          lessons: [
            {
              id: 'python-day-1-fundamentals',
              title: 'Dicts, Lists, Tuples + Comprehensions',
              intro: 'AI payloads are just structured data. Master the shapes and you control the model inputs.',
              logic: 'Use dict/list/tuple transforms, unpacking, and comprehensions to normalize a JSON payload into a clean feature bundle.',
              code: `def normalize_payload(payload):\n    user = payload.get('user', {})\n    prefs = user.get('prefs', {})\n    scores = payload.get('scores', [])\n    tags = list({tag.strip().lower() for tag in payload.get('tags', []) if tag})\n\n    first_name, last_name = (user.get('name', '').split(' ') + [''])[:2]\n    score_sum = sum(score.get('value', 0) for score in scores)\n\n    return {\n        'user_id': user.get('id'),\n        'name': f\"{first_name} {last_name}\".strip(),\n        'region': prefs.get('region', 'unknown'),\n        'channels': [channel for channel in prefs.get('channels', [])],\n        'score_sum': score_sum,\n        'tags': tags\n    }`,
              problem: 'Exercise: parse and reshape a JSON payload into a clean training-ready dictionary.',
              task: 'Normalize nested keys, flatten scores, and keep a unique list of tags.',
              tips: [
                'Use .get with defaults to avoid KeyError.',
                'Unpack a list into first/last name safely.',
                'Comprehensions are perfect for filtering null tags.',
                'Remember: sets dedupe, lists preserve order.',
                'Always return a flat dict for model inputs.'
              ],
              demoScript: [
                'Demo: Payload reshaping',
                'We clean nested keys and flatten scores.',
                'Comprehensions keep the code tight.',
                'def normalize_payload(payload):',
                '    user = payload.get("user", {})',
                '    prefs = user.get("prefs", {})',
                '    scores = payload.get("scores", [])',
                '    tags = list({tag.strip().lower() for tag in payload.get("tags", []) if tag})',
                '    first_name, last_name = (user.get("name", "").split(" ") + [""])[:2]',
                '    score_sum = sum(score.get("value", 0) for score in scores)',
                '    return {"user_id": user.get("id"), "name": f"{first_name} {last_name}".strip(), "region": prefs.get("region", "unknown"), "channels": [channel for channel in prefs.get("channels", [])], "score_sum": score_sum, "tags": tags}',
                'Try: feed the sample JSON and inspect the normalized dict.'
              ]
            }
          ]
        },
        {
          id: 'python-day-2',
          title: 'Day 2 — Types for scale',
          subtitle: 'Typed contracts make AI systems debuggable.',
          lessons: [
            {
              id: 'python-day-2-types',
              title: 'TypedDict + dataclass payloads',
              intro: 'Types are your safety harness when your AI app scales.',
              logic: 'Define request/response contracts with TypedDict, Optional, and Dict[str, Any] to ensure clarity at the boundary.',
              code: `from dataclasses import dataclass\nfrom typing import Dict, Any, Optional, TypedDict\n\nclass CodeGemRequest(TypedDict):\n    user_id: str\n    prompt: str\n    metadata: Optional[Dict[str, Any]]\n\nclass CodeGemResponse(TypedDict):\n    output: str\n    tokens_used: int\n    debug: Optional[Dict[str, Any]]\n\n@dataclass\nclass CodeGemPayload:\n    request: CodeGemRequest\n    response: CodeGemResponse`,
              problem: 'Exercise: type a request/response contract like your CodeGem payload.',
              task: 'Model input metadata as optional and ensure output debug details are typed.',
              tips: [
                'TypedDict is for JSON-like dicts.',
                'Optional[T] means the value can be None.',
                'Use Dict[str, Any] for flexible metadata.',
                'Dataclasses make nested payloads readable.',
                'Keep contracts close to API boundaries.'
              ],
              demoScript: [
                'Demo: Type contracts',
                'We use TypedDict for JSON boundaries.',
                'Dataclass groups request/response together.',
                'class CodeGemRequest(TypedDict):',
                '    user_id: str',
                '    prompt: str',
                '    metadata: Optional[Dict[str, Any]]',
                'class CodeGemResponse(TypedDict):',
                '    output: str',
                '    tokens_used: int',
                '    debug: Optional[Dict[str, Any]]',
                'Ship typed contracts with every API change.'
              ]
            }
          ]
        },
        {
          id: 'python-day-3',
          title: 'Day 3 — HTTP + APIs',
          subtitle: 'Requests, retries, and clean JSON parsing.',
          lessons: [
            {
              id: 'python-day-3-http',
              title: 'urllib vs requests + retry logic',
              intro: 'Calling AI services means living in the messy world of HTTP.',
              logic: 'Compare urllib and requests. Parse JSON with guards and add retry logic for transient errors.',
              code: `import json\nimport time\nfrom urllib import request\n\n\ndef fetch_with_retry(url, retries=3):\n    for attempt in range(1, retries + 1):\n        try:\n            with request.urlopen(url, timeout=5) as resp:\n                data = json.loads(resp.read().decode('utf-8'))\n                return data\n        except Exception:\n            if attempt == retries:\n                raise\n            time.sleep(0.5 * attempt)\n\n    return {}`,
              problem: 'Exercise: call a mock endpoint and handle retries.',
              task: 'Retry on failures and parse JSON safely.',
              tips: [
                'Wrap HTTP calls in a retry loop.',
                'Always decode bytes before json.loads.',
                'Use exponential backoff for stability.',
                'Surface errors after the final retry.',
                'Log the attempt count in production.'
              ],
              demoScript: [
                'Demo: HTTP with retries',
                'We call a mock endpoint and parse JSON.',
                'for attempt in range(1, retries + 1):',
                '    try:',
                '        with request.urlopen(url, timeout=5) as resp:',
                '            return json.loads(resp.read().decode("utf-8"))',
                '    except Exception:',
                '        time.sleep(0.5 * attempt)',
                'Try: fail twice and confirm the third attempt returns data.'
              ]
            }
          ]
        },
        {
          id: 'python-day-4',
          title: 'Day 4 — Flask “ship it” API',
          subtitle: 'Routes, validation, and production-leaning responses.',
          lessons: [
            {
              id: 'python-day-4-flask',
              title: 'Flask routes + validation',
              intro: 'A quick Flask API is still the fastest way to ship a demo.',
              logic: 'Create POST /tutor and GET /health, validate input, and return structured JSON.',
              code: `from flask import Flask, request, jsonify\n\napp = Flask(__name__)\n\n@app.get('/health')\ndef health():\n    return jsonify({'status': 'ok'})\n\n@app.post('/tutor')\ndef tutor():\n    payload = request.get_json(silent=True) or {}\n    prompt = payload.get('prompt')\n    if not prompt:\n        return jsonify({'error': 'prompt is required'}), 400\n    return jsonify({'answer': f\"Explaining: {prompt}\"})`,
              problem: 'Exercise: build /tutor and /health endpoints.',
              task: 'Return 400 on missing prompt, and respond with JSON.',
              tips: [
                'Use request.get_json(silent=True) to avoid crashes.',
                'Return tuples for status codes.',
                'Keep health checks fast and simple.',
                'Validate required keys before processing.',
                'Wrap business logic in a separate function.'
              ],
              demoScript: [
                'Demo: Flask ship-it API',
                'GET /health -> {"status":"ok"}',
                'POST /tutor with {"prompt":"Explain RNNs"}',
                'Return a JSON response with the answer field.',
                'Remember: validate before processing.'
              ]
            }
          ]
        },
        {
          id: 'python-day-5',
          title: 'Day 5 — Lambda reality',
          subtitle: 'Events, headers, and keeping timeouts happy.',
          lessons: [
            {
              id: 'python-day-5-lambda',
              title: 'Lambda handler conversion',
              intro: 'AWS Lambda uses event objects, not Flask request objects.',
              logic: 'Parse headers, base64 bodies, and query strings to deliver the same API in serverless form.',
              code: `import base64\nimport json\n\n\ndef lambda_handler(event, _context):\n    body = event.get('body') or ''\n    if event.get('isBase64Encoded'):\n        body = base64.b64decode(body).decode('utf-8')\n    payload = json.loads(body) if body else {}\n\n    prompt = payload.get('prompt')\n    if not prompt:\n        return {'statusCode': 400, 'body': json.dumps({'error': 'prompt is required'})}\n\n    return {'statusCode': 200, 'body': json.dumps({'answer': f\"Explaining: {prompt}\"})}`,
              problem: 'Exercise: convert your Flask handler into Lambda handler style.',
              task: 'Support base64 payloads and return statusCode/body.',
              tips: [
                'Lambda event bodies are strings by default.',
                'Check isBase64Encoded before parsing JSON.',
                'Return a dict with statusCode and body.',
                'Be strict on required inputs.',
                'Watch timeout settings in production.'
              ],
              demoScript: [
                'Demo: Lambda handler',
                'Decode body if base64.',
                'Parse JSON and validate prompt.',
                'Return {"statusCode":200,"body":"..."}',
                'Plug into API Gateway to match Flask behavior.'
              ]
            }
          ]
        },
        {
          id: 'python-day-6',
          title: 'Day 6 — OCR pipeline',
          subtitle: 'Image/PDF to text, then normalize key fields.',
          lessons: [
            {
              id: 'python-day-6-ocr',
              title: 'OCR to postprocess',
              intro: 'OCR output is noisy. Clean it before downstream AI.',
              logic: 'Run OCR, extract totals and dates, and normalize values for downstream use.',
              code: `import re\nfrom datetime import datetime\n\n\ndef extract_invoice_fields(text):\n    total_match = re.search(r'Total\\s*[:$]?\\s*(\\d+[\\d,.]*)', text, re.IGNORECASE)\n    date_match = re.search(r'(\\d{2}/\\d{2}/\\d{4})', text)\n\n    total = float(total_match.group(1).replace(',', '')) if total_match else None\n    date = datetime.strptime(date_match.group(1), '%m/%d/%Y').date() if date_match else None\n\n    return {'total': total, 'date': date}`,
              problem: 'Exercise: extract totals/dates from OCR text.',
              task: 'Parse currency and dates, then return structured fields.',
              tips: [
                'Regex is your friend for OCR cleanup.',
                'Normalize commas before float conversion.',
                'Use datetime for date validation.',
                'Return None when fields are missing.',
                'Add vendor extraction as a bonus.'
              ],
              demoScript: [
                'Demo: OCR postprocess',
                'Input: OCR text blob.',
                'Regex find Total and date.',
                'Return {"total": 123.45, "date": 2024-05-02}',
                'Extend to vendor + invoice number next.'
              ]
            }
          ]
        },
        {
          id: 'python-day-7',
          title: 'Day 7 — Business outputs',
          subtitle: 'Summaries and charts that stakeholders understand.',
          lessons: [
            {
              id: 'python-day-7-reporting',
              title: 'Pandas summaries + matplotlib',
              intro: 'AI results need business-ready outputs.',
              logic: 'Aggregate invoice totals by vendor, then render a simple chart.',
              code: `import pandas as pd\nimport matplotlib.pyplot as plt\n\n\ndef build_vendor_report(records):\n    df = pd.DataFrame(records)\n    summary = df.groupby('vendor', as_index=False)['total'].sum()\n    summary = summary.sort_values('total', ascending=False)\n\n    ax = summary.plot.bar(x='vendor', y='total', legend=False)\n    ax.set_ylabel('Total ($)')\n    ax.set_title('Invoice Totals by Vendor')\n    plt.tight_layout()\n\n    return summary`,
              problem: 'Exercise: build “invoice totals by vendor” report.',
              task: 'Group by vendor, sum totals, and chart the output.',
              tips: [
                'Use groupby + sum for aggregation.',
                'Sort totals to highlight top vendors.',
                'Keep charts simple and readable.',
                'Return the summary table for exports.',
                'Save the figure in production pipelines.'
              ],
              demoScript: [
                'Demo: Business reporting',
                'records = [{"vendor":"Acme","total":120.0}, {"vendor":"Zeta","total":90.0}]',
                'summary = build_vendor_report(records)',
                'Plot the chart and share the summary table.',
                'Stakeholders love sorted totals.'
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'art-design-python',
      title: 'Art & Design',
      description: 'Gemini - Art and Design: Python can be used to create digital art, interactive projects, and rapid visual experiments.',
      levels: [
        {
          id: 'art-design-studio',
          title: 'Studio Projects',
          subtitle: 'Five creative builds that blend Python, visuals, and real-world inspiration.',
          lessons: [
            {
              id: 'gradient-poster',
              title: 'Gradient Poster Generator',
              intro: 'Real-world example: 2024 music festivals ship dozens of poster variants with the same layout but different gradient palettes.',
              logic: 'Use Pillow + NumPy to blend a two-color gradient, then stamp a bold title for a digital poster.',
              code: `from PIL import Image, ImageDraw, ImageFont\nimport numpy as np\n\n\ndef make_poster(width=1080, height=1350, top=(37, 99, 235), bottom=(238, 110, 111)):\n    gradient = np.zeros((height, width, 3), dtype=np.uint8)\n    for y in range(height):\n        t = y / (height - 1)\n        gradient[y, :, :] = (np.array(top) * (1 - t) + np.array(bottom) * t).astype(np.uint8)\n\n    image = Image.fromarray(gradient)\n    draw = ImageDraw.Draw(image)\n    draw.rectangle((90, 220, width - 90, 320), outline=(255, 255, 255), width=4)\n    draw.text((110, 235), \"NEON STUDIO\", fill=(255, 255, 255))\n    return image\n\nposter = make_poster()\nposter.save(\"poster.png\")`,
              problem: 'Coding challenge: generate three poster colorways for a pop-up gallery.',
              task: 'Swap the top/bottom colors to create three unique gradients and save each poster.',
              tips: [
                'Pick a two-color palette that mirrors a real brand mood board.',
                'Keep typography centered so the gradient carries the style.',
                'Use outline rectangles to frame the headline.',
                'Export at 1080x1350 for social post sizing.',
                'Name files with the palette so you can A/B test later.'
              ],
              demoScript: [
                'Typing demo: Gradient poster',
                'from PIL import Image, ImageDraw',
                'import numpy as np',
                'def make_poster(top, bottom):',
                '    # Blend colors line-by-line',
                '    # Save as poster.png',
                'Animating colors as the gradient shifts...',
                'Save three variants and compare vibes.'
              ],
              dataset: 'Palette set: [(37,99,235),(238,110,111)], [(48,201,160),(100,43,115)], [(255,176,59),(255,64,129)].'
            },
            {
              id: 'particle-brush',
              title: 'Interactive Particle Brush',
              intro: 'Real-world example: 2023–2024 museum installations let visitors paint light trails that react to motion.',
              logic: 'Use Pygame to spawn particles from the cursor and animate them with velocity + fade-out.',
              code: `import pygame\nimport random\n\npygame.init()\nscreen = pygame.display.set_mode((900, 600))\nclock = pygame.time.Clock()\nparticles = []\n\nrunning = True\nwhile running:\n    for event in pygame.event.get():\n        if event.type == pygame.QUIT:\n            running = False\n\n    x, y = pygame.mouse.get_pos()\n    for _ in range(6):\n        particles.append([x, y, random.uniform(-2, 2), random.uniform(-2, 2), 255])\n\n    screen.fill((15, 15, 25))\n    for p in particles[:]:\n        p[0] += p[2]\n        p[1] += p[3]\n        p[4] -= 4\n        if p[4] <= 0:\n            particles.remove(p)\n        else:\n            pygame.draw.circle(screen, (120, 220, 255, p[4]), (int(p[0]), int(p[1])), 4)\n\n    pygame.display.flip()\n    clock.tick(60)`,
              problem: 'Coding challenge: make the brush react to click speed with bigger bursts.',
              task: 'Measure cursor velocity and scale particle count by speed.',
              tips: [
                'Spawn more particles when the cursor moves faster.',
                'Fade alpha to keep the trail dreamy.',
                'Cap the particle list to avoid frame drops.',
                'Try blending modes for neon glow.',
                'Record a GIF for your portfolio.'
              ],
              demoScript: [
                'Typing demo: Particle brush',
                'particles.append([x, y, vx, vy, 255])',
                'Animate: update position + fade alpha.',
                'Add speed-based bursts for wow.',
                'You are basically painting with physics.'
              ],
              dataset: 'Try speed thresholds: [0, 2, 5, 9] -> particle counts [3, 6, 10, 16].'
            },
            {
              id: 'kinetic-typography',
              title: 'Kinetic Typography SVG',
              intro: 'Real-world example: 2024 brand launches use SVG motion type for lightweight landing pages.',
              logic: 'Generate an SVG with Python and include an <animate> tag for oscillating text.',
              code: `import svgwrite\n\nsvg = svgwrite.Drawing('kinetic.svg', size=('800px', '300px'))\ntext = svg.text('MOVE WITH SOUND', insert=(80, 160), fill='#ffffff', font_size='48px', font_family='Arial Black')\ntext.add(svg.animate(attributeName='x', values='80;140;80', dur='3s', repeatCount='indefinite'))\nsvg.add(svg.rect(insert=(0, 0), size=('800px', '300px'), fill='#111827'))\nsvg.add(text)\nsvg.save()`,
              problem: 'Coding challenge: create two timing curves for subtle and bold motion.',
              task: 'Adjust animation durations and add a second text layer for depth.',
              tips: [
                'Keep the motion loop under 4 seconds for a calm tempo.',
                'Use two text layers with different delays.',
                'Use high-contrast backgrounds for readability.',
                'Export SVG for crisp scaling in responsive layouts.',
                'Open in the browser to preview the animation.'
              ],
              demoScript: [
                'Typing demo: SVG motion type',
                'text.add(svg.animate(attributeName="x", values="80;140;80"))',
                'Animate: loop the x-position for kinetic energy.',
                'Add a second layer for parallax.',
                'Ship it to a landing page.'
              ],
              dataset: 'Motion presets: subtle=4s, bold=2s. Try delays: 0s, 0.6s.'
            },
            {
              id: 'audio-waveform',
              title: 'Audio-Reactive Wave Animation',
              intro: 'Real-world example: 2024 live concert visuals use audio-reactive waves synced to beats.',
              logic: 'Use Matplotlib animation to render a waveform that shifts with pseudo-audio amplitude.',
              code: `import numpy as np\nimport matplotlib.pyplot as plt\nfrom matplotlib.animation import FuncAnimation\n\nfig, ax = plt.subplots()\nline, = ax.plot([], [], lw=3)\nax.set_xlim(0, 2 * np.pi)\nax.set_ylim(-2, 2)\n\nx = np.linspace(0, 2 * np.pi, 200)\n\ndef update(frame):\n    amp = 0.5 + 0.5 * np.sin(frame / 5)\n    y = amp * np.sin(x * 3 + frame / 10)\n    line.set_data(x, y)\n    return line,\n\nani = FuncAnimation(fig, update, frames=200, interval=30)\nani.save('wave.gif', writer='pillow')`,
              problem: 'Coding challenge: add a second sine wave layer with a different color.',
              task: 'Plot two lines and animate both with different frequencies.',
              tips: [
                'Animate small parameter changes for smooth visuals.',
                'Pick color pairs that complement your brand palette.',
                'Export as GIF for quick sharing.',
                'Keep axes hidden for a clean presentation.',
                'Swap sine with noise for glitchy effects.'
              ],
              demoScript: [
                'Typing demo: Wave animation',
                'ani = FuncAnimation(fig, update, frames=200)',
                'Animate amplitude for a pulsing effect.',
                'Add a second line to feel stereo.',
                'Render to wave.gif.'
              ],
              dataset: 'Frequencies to try: [2, 3, 5], amplitude range: 0.2–1.2.'
            },
            {
              id: 'ascii-mural',
              title: 'ASCII Mural + Typing Reveal',
              intro: 'Real-world example: 2024 developer livestreams use ASCII art animations as terminal backdrops.',
              logic: 'Use Rich to animate ASCII frames and simulate a typing reveal for each frame.',
              code: `from rich.console import Console\nfrom rich.live import Live\nfrom time import sleep\n\nframes = [\n    \"\"\"  /\\_/\\\\\\n ( o.o )\\n  > ^ < \"\"\",\n    \"\"\"  /\\_/\\\\\\n ( -.- )\\n  > ^ < \"\"\"\n]\n\nconsole = Console()\nwith Live(frames[0], console=console, refresh_per_second=8) as live:\n    for frame in frames * 4:\n        live.update(frame)\n        sleep(0.15)`,
              problem: 'Coding challenge: create a 3-frame mural that types in line-by-line.',
              task: 'Loop through each line and add a short delay to mimic a live typing animation.',
              tips: [
                'Use short frames to keep the animation crisp.',
                'Delay line-by-line for a true typing feel.',
                'Cycle through frames to give the mural life.',
                'Export recordings for social snippets.',
                'Combine with a color palette using Rich styles.'
              ],
              demoScript: [
                'Typing demo: ASCII mural',
                'with Live(frame) as live:',
                '    live.update(frame)',
                'Animate frames and simulate typing.',
                'Perfect for terminal art stingers.'
              ],
              dataset: 'Frame idea: build a 3-panel owl with blinking eyes.'
            }
          ]
        }
      ]
    },
    {
      id: 'html',
      title: 'HTML',
      description: 'From first tags to semantic structure, following the Clarichat learning flow.',
      levels: [
        {
          id: 'html-beginner',
          title: 'Beginner',
          subtitle: 'Lay out your first pages and media elements.',
          lessons: [
            {
              id: 'html-basic-page',
              title: 'Basic Page',
              intro: 'Build the core document structure every HTML page starts with.',
              logic: 'Use the standard HTML skeleton with headings and paragraphs.',
              code: `<!DOCTYPE html>\n<html>\n<head>\n  <title>Page Title</title>\n</head>\n<body>\n  <h1>My First Heading</h1>\n  <p>My first paragraph.</p>\n</body>\n</html>`,
              problem: 'Create a simple HTML page with a title, heading, and paragraph.',
              task: 'Follow the HTML structure and replace the content with your own text.',
              tips: [
                'Always include the <!DOCTYPE html> declaration.',
                'Headings go inside <body>.',
                'Use <title> for the browser tab label.',
                'Keep tags properly nested and closed.',
                'Preview the page in a browser to verify layout.'
              ],
              demoScript: [
                'Lesson: Basic HTML Page',
                'Start with the DOCTYPE and html tags.',
                'Add a head with a title.',
                'Drop in a heading + paragraph.',
                'Save and open in the browser.'
              ],
              dataset: 'Starter idea: Build a welcome page for your portfolio.'
            },
            {
              id: 'html-links-images',
              title: 'Links & Images',
              intro: 'Add navigation and visuals to your page with anchors and images.',
              logic: 'Use the <a> tag for hyperlinks and <img> for images with alt text.',
              code: `<a href="https://www.clarichat.com">Visit Clarichat</a>\n\n<img src="img_girl.jpg" alt="Girl with a jacket" width="500" height="600">`,
              problem: 'Add a clickable link and an image that includes alt text and sizing.',
              task: 'Update the href and image source to match your own assets.',
              tips: [
                'Use descriptive link text for clarity.',
                'Always include alt text for accessibility.',
                'Set width/height to avoid layout shift.',
                'Test the link in a new tab before shipping.',
                'Keep images optimized for faster load times.'
              ],
              demoScript: [
                'Lesson: Links & Images',
                'Add an anchor tag for navigation.',
                'Include an image with alt text.',
                'Set width and height attributes.',
                'Refresh the page to see the media.'
              ],
              dataset: 'Challenge: Link to your docs and add a hero image.'
            }
          ]
        },
        {
          id: 'html-intermediate',
          title: 'Intermediate',
          subtitle: 'Structure data and collect input with forms.',
          lessons: [
            {
              id: 'html-lists-tables',
              title: 'Lists & Tables',
              intro: 'Organize information with clean lists and tables.',
              logic: 'Use <ul>/<ol> for lists and <table> for structured data.',
              code: `<ul>\n  <li>Coffee</li>\n  <li>Tea</li>\n  <li>Milk</li>\n</ul>\n\n<table>\n  <tr>\n    <th>Company</th>\n    <th>Contact</th>\n    <th>Country</th>\n  </tr>\n  <tr>\n    <td>Alfreds Futterkiste</td>\n    <td>Maria Anders</td>\n    <td>Germany</td>\n  </tr>\n</table>`,
              problem: 'Display a simple list and a data table on the same page.',
              task: 'Swap in your own list items and table data.',
              tips: [
                'Lists work great for navigation and checklists.',
                'Use <th> for table headings.',
                'Keep table rows consistent.',
                'Add borders in CSS for readability.',
                'Use <caption> if the table needs a title.'
              ],
              demoScript: [
                'Lesson: Lists & Tables',
                'Draft an unordered list.',
                'Create a table header row.',
                'Add a table data row.',
                'Style it once structure is correct.'
              ],
              dataset: 'Challenge: Show your top 3 tools and a client table.'
            },
            {
              id: 'html-forms',
              title: 'Forms',
              intro: 'Collect user input with forms, labels, and buttons.',
              logic: 'Pair <label> tags with inputs and add a submit control.',
              code: `<form action="/action_page.php">\n  <label for="fname">First name:</label><br>\n  <input type="text" id="fname" name="fname"><br>\n  <label for="lname">Last name:</label><br>\n  <input type="text" id="lname" name="lname"><br><br>\n  <input type="submit" value="Submit">\n</form>`,
              problem: 'Build a basic form that collects first and last name.',
              task: 'Add your own fields and adjust the form action.',
              tips: [
                'Labels improve accessibility and click targets.',
                'Give inputs unique id and name attributes.',
                'Use <br> or CSS for spacing.',
                'Pick the right input type for data.',
                'Test submission to confirm data is sent.'
              ],
              demoScript: [
                'Lesson: Forms',
                'Start with a <form> element.',
                'Add labels for each input.',
                'Include a submit button.',
                'Test with sample entries.'
              ],
              dataset: 'Challenge: Add an email field and a sign-up checkbox.'
            }
          ]
        },
        {
          id: 'html-advanced',
          title: 'Advanced',
          subtitle: 'Make your markup semantic and future-proof.',
          lessons: [
            {
              id: 'html-semantic',
              title: 'Semantic Layout',
              intro: 'Create structure that helps browsers, readers, and assistive tech.',
              logic: 'Use semantic tags like <header>, <nav>, <section>, and <footer>.',
              code: `<header>\n  <h1>City Gallery</h1>\n</header>\n\n<nav>\n  <a href="#london">London</a>\n  <a href="#paris">Paris</a>\n  <a href="#tokyo">Tokyo</a>\n</nav>\n\n<section>\n  <article id="london">\n    <h2>London</h2>\n    <p>London is the capital city of England.</p>\n  </article>\n</section>\n\n<footer>\n  <p>Footer content</p>\n</footer>`,
              problem: 'Organize a page using semantic tags for navigation and content.',
              task: 'Replace the sample cities with your own sections.',
              tips: [
                'Semantic tags help SEO and accessibility.',
                'Group navigation in <nav>.',
                'Use <article> for self-contained content.',
                'Keep headings in logical order.',
                'Add CSS to differentiate sections.'
              ],
              demoScript: [
                'Lesson: Semantic Layout',
                'Add header and nav elements.',
                'Place main content inside sections.',
                'Close with a footer.',
                'Run an accessibility check.'
              ],
              dataset: 'Challenge: Outline a product landing page semantically.'
            }
          ]
        }
      ]
    },
    {
      id: 'css',
      title: 'CSS',
      description: 'Style with Clarichat utility classes and quick layout helpers.',
      levels: [
        {
          id: 'css-beginner',
          title: 'Beginner',
          subtitle: 'Color, spacing, and simple components.',
          lessons: [
            {
              id: 'w3css-container',
              title: 'Containers & Colors',
              intro: 'Use Clarichat containers to style a hero section fast.',
              logic: 'Combine clarichat-container with color classes to build structure.',
              code: `<div class="clarichat-container clarichat-teal">\n  <h1>My Header</h1>\n</div>\n\n<div class="clarichat-container">\n  <p>My paragraph.</p>\n</div>`,
              problem: 'Create a colored header with a paragraph block beneath it.',
              task: 'Swap the color class and text to match your brand.',
              tips: [
                'Clarichat color classes are quick for prototypes.',
                'Use clarichat-container for padding and alignment.',
                'Stack containers to create sections.',
                'Pair with custom CSS for fine-tuning.',
                'Check contrast for readability.'
              ],
              demoScript: [
                'Lesson: Containers & Colors',
                'Wrap content with clarichat-container.',
                'Add a clarichat-teal background.',
                'Drop in a headline and paragraph.',
                'Refresh the page for instant styling.'
              ],
              dataset: 'Challenge: Create a two-section intro page.'
            },
            {
              id: 'w3css-cards',
              title: 'Cards',
              intro: 'Create card layouts for profiles or product previews.',
              logic: 'Use clarichat-card classes to add depth and spacing.',
              code: `<div class="clarichat-card-4" style="width:50%">\n  <img src="img_avatar3.png" alt="Avatar" style="width:100%">\n  <div class="clarichat-container">\n    <h4><b>John Doe</b></h4>\n    <p>Architect & Engineer</p>\n  </div>\n</div>`,
              problem: 'Design a simple profile card with an image and text.',
              task: 'Update the avatar and details to fit your content.',
              tips: [
                'Keep images the same width for alignment.',
                'Use clarichat-card-4 for deeper shadow.',
                'Cards are great for product listings.',
                'Limit text to keep cards compact.',
                'Add a button for actions.'
              ],
              demoScript: [
                'Lesson: Cards',
                'Start with a clarichat-card-4 wrapper.',
                'Drop in an image at full width.',
                'Add a container for text.',
                'Try a second card for a grid.'
              ],
              dataset: 'Challenge: Build a three-card team row.'
            }
          ]
        },
        {
          id: 'css-intermediate',
          title: 'Intermediate',
          subtitle: 'Buttons, grids, and layout helpers.',
          lessons: [
            {
              id: 'w3css-buttons',
              title: 'Buttons',
              intro: 'Create strong calls to action with Clarichat button styles.',
              logic: 'Buttons combine clarichat-button with color, size, or hover classes.',
              code: `<button class="clarichat-button clarichat-blue">Button</button>\n<button class="clarichat-button clarichat-green clarichat-round-large">Success</button>\n<button class="clarichat-button clarichat-border">Ghost</button>`,
              problem: 'Add primary, success, and ghost buttons to a UI section.',
              task: 'Pick colors that align with your palette.',
              tips: [
                'Use clarichat-round-large for softer corners.',
                'Combine clarichat-border for outlined styles.',
                'Keep button text short and active.',
                'Align buttons with a flex container.',
                'Test hover states for accessibility.'
              ],
              demoScript: [
                'Lesson: Buttons',
                'Add a primary button.',
                'Create a rounded success button.',
                'Finish with a bordered ghost button.',
                'Align them in a row.'
              ],
              dataset: 'Challenge: Build a button row for pricing.'
            },
            {
              id: 'w3css-grid',
              title: 'Responsive Grid',
              intro: 'Lay out content in columns that respond to screen size.',
              logic: 'Use clarichat-row and clarichat-col classes to define responsive columns.',
              code: `<div class="clarichat-row">\n  <div class="clarichat-col m6 clarichat-padding">\n    <div class="clarichat-card clarichat-padding">Left column</div>\n  </div>\n  <div class="clarichat-col m6 clarichat-padding">\n    <div class="clarichat-card clarichat-padding">Right column</div>\n  </div>\n</div>`,
              problem: 'Create a two-column layout that stacks on mobile.',
              task: 'Swap the content to feature your own sections.',
              tips: [
                'm6 means half-width on medium screens.',
                'Add clarichat-padding to separate columns.',
                'Use clarichat-card for surface elevation.',
                'Test on mobile widths for stacking.',
                'Consider m4 or m8 for uneven layouts.'
              ],
              demoScript: [
                'Lesson: Responsive Grid',
                'Add a clarichat-row wrapper.',
                'Split into two clarichat-col m6 columns.',
                'Insert card content inside.',
                'Resize to confirm stacking.'
              ],
              dataset: 'Challenge: Build a three-column feature row.'
            }
          ]
        },
        {
          id: 'css-advanced',
          title: 'Advanced',
          subtitle: 'Interactive components and overlays.',
          lessons: [
            {
              id: 'w3css-modal',
              title: 'Modal Dialog',
              intro: 'Create a modal overlay for alerts or forms.',
              logic: 'Use clarichat-modal and clarichat-modal-content to build the overlay.',
              code: `<button onclick="document.getElementById('id01').style.display='block'" class="clarichat-button clarichat-black">Open Modal</button>\n\n<div id="id01" class="clarichat-modal">\n  <div class="clarichat-modal-content clarichat-animate-top clarichat-card-4">\n    <header class="clarichat-container clarichat-teal">\n      <span onclick="document.getElementById('id01').style.display='none'" class="clarichat-button clarichat-display-topright">&times;</span>\n      <h2>Modal Header</h2>\n    </header>\n    <div class="clarichat-container">\n      <p>Some text in the Modal..</p>\n    </div>\n  </div>\n</div>`,
              problem: 'Build a modal that opens and closes with a button click.',
              task: 'Update the modal content to match your use case.',
              tips: [
                'Remember to hide the modal by default.',
                'Use clarichat-animate-top for entry animation.',
                'Place close buttons inside the header.',
                'Trap focus for accessibility if needed.',
                'Test on mobile for overflow.'
              ],
              demoScript: [
                'Lesson: Modal Dialog',
                'Add the trigger button.',
                'Create the modal wrapper.',
                'Insert modal content with header.',
                'Add close controls and test.'
              ],
              dataset: 'Challenge: Use the modal for a newsletter signup.'
            }
          ]
        }
      ]
    },
    {
      id: 'javascript',
      title: 'JavaScript',
      description: 'Beginner to advanced JavaScript tasks aligned with Clarichat tutorials.',
      levels: [
        {
          id: 'js-beginner',
          title: 'Beginner',
          subtitle: 'Output, variables, and the basics.',
          lessons: [
            {
              id: 'js-output',
              title: 'Output to the DOM',
              intro: 'Print your first message into the page.',
              logic: 'Select an element and set innerHTML for quick feedback.',
              code: `document.getElementById("demo").innerHTML = "Hello JavaScript";`,
              problem: 'Display a greeting message inside a paragraph with id="demo".',
              task: 'Change the message to match your theme.',
              tips: [
                'Make sure the element id exists in HTML.',
                'Use innerHTML for quick text output.',
                'Keep JavaScript at the bottom of the page or in a script file.',
                'Wrap in a function if you need reuse.',
                'Try console.log for debugging.'
              ],
              demoScript: [
                'Lesson: Output to the DOM',
                'Select the #demo element.',
                'Update innerHTML.',
                'Refresh the page.',
                'Confirm the new text appears.'
              ],
              dataset: 'Challenge: Show a personalized greeting using a name variable.'
            },
            {
              id: 'js-variables',
              title: 'Variables',
              intro: 'Store data and compute values with variables.',
              logic: 'Use let to declare values and combine them.',
              code: `let x = 5;\nlet y = 6;\nlet z = x + y;`,
              problem: 'Declare variables and compute their sum.',
              task: 'Replace the numbers with your own values.',
              tips: [
                'Use let for values that change.',
                'Keep variable names descriptive.',
                'Use const for fixed values.',
                'Log results to verify calculations.',
                'Group related variables together.'
              ],
              demoScript: [
                'Lesson: Variables',
                'Declare x and y.',
                'Add them into z.',
                'Log the result.',
                'Update values and re-run.'
              ],
              dataset: 'Challenge: Compute the total price with tax.'
            }
          ]
        },
        {
          id: 'js-intermediate',
          title: 'Intermediate',
          subtitle: 'Functions and user events.',
          lessons: [
            {
              id: 'js-functions',
              title: 'Functions',
              intro: 'Group logic into reusable functions.',
              logic: 'Declare a function and return a calculated value.',
              code: `function myFunction(p1, p2) {\n  return p1 * p2;\n}`,
              problem: 'Create a function that multiplies two numbers.',
              task: 'Call the function with your own arguments.',
              tips: [
                'Return values for reuse.',
                'Name functions with verbs.',
                'Keep functions small and focused.',
                'Test with multiple inputs.',
                'Use arrow functions once comfortable.'
              ],
              demoScript: [
                'Lesson: Functions',
                'Define myFunction.',
                'Return the product.',
                'Call it with numbers.',
                'Display the result.'
              ],
              dataset: 'Challenge: Build a function that formats a full name.'
            },
            {
              id: 'js-events',
              title: 'Events',
              intro: 'Respond to user clicks with event handlers.',
              logic: 'Attach an onclick event to update the DOM.',
              code: `document.getElementById("myBtn").onclick = function() {\n  document.getElementById("demo").innerHTML = Date();\n};`,
              problem: 'Update the page when a button is clicked.',
              task: 'Change the output to show a custom message.',
              tips: [
                'Ensure the button id matches the selector.',
                'Wrap event handlers in functions.',
                'Avoid inline onclick when possible.',
                'Use addEventListener for multiple events.',
                'Test on both click and keyboard.'
              ],
              demoScript: [
                'Lesson: Events',
                'Select the button.',
                'Attach an onclick handler.',
                'Update the demo text.',
                'Click to verify behavior.'
              ],
              dataset: 'Challenge: Toggle dark mode on click.'
            }
          ]
        },
        {
          id: 'js-advanced',
          title: 'Advanced',
          subtitle: 'Loops, arrays, and dynamic output.',
          lessons: [
            {
              id: 'js-arrays-loop',
              title: 'Arrays & Loops',
              intro: 'Loop over data and build dynamic output.',
              logic: 'Use a for loop to render each array item.',
              code: `const cars = ["Saab", "Volvo", "BMW"];\nlet text = "";\nfor (let i = 0; i < cars.length; i++) {\n  text += cars[i] + "<br>";\n}`,
              problem: 'Display a list of array values on the page.',
              task: 'Replace the array with your own data.',
              tips: [
                'Initialize output before the loop.',
                'Use <br> for line breaks or build a list.',
                'Check loop boundaries to avoid errors.',
                'Use for...of for cleaner iteration.',
                'Consider map() for transformations.'
              ],
              demoScript: [
                'Lesson: Arrays & Loops',
                'Declare the array.',
                'Initialize an empty string.',
                'Loop and append each item.',
                'Render the final text.'
              ],
              dataset: 'Challenge: Render your top 5 tasks as a list.'
            }
          ]
        }
      ]
    }
  ];

  activeCategoryIndex = 0;
  activeLevelIndex = 0;
  activeLessonIndex = 0;
  datasets: Record<string, string> = {};
  typedDemo = '';
  isTyping = false;
  revealedTips = 1;
  skillLevel = 'Beginner';
  passionLevel = 3;
  userSolution = '';
  messages: ChatMessage[] = [];
  isSubmitting = false;

  private typingTimer?: number;
  private tipsTimer?: number;

  private converter = new showdown.Converter({
    tables: true,
    strikethrough: true,
    tasklists: true,
    ghCompatibleHeaderId: true,
    extensions: [showdownMarkdownClassesExtension({ textClass: 'md-text' })]
  });

  constructor(private codeGem: CodeGemService) {}

  get activeCategory(): CourseCategory {
    return this.categories[this.activeCategoryIndex];
  }

  get activeLevel(): LevelGroup {
    return this.activeCategory.levels[this.activeLevelIndex];
  }

  get activeLesson(): Lesson {
    return this.activeLevel.lessons[this.activeLessonIndex];
  }

  get activeDataset(): string {
    return this.datasets[this.activeLesson.id] || '';
  }

  get visibleTips(): string[] {
    return this.activeLesson.tips.slice(0, this.revealedTips);
  }

  get allTipsRevealed(): boolean {
    return this.revealedTips >= this.activeLesson.tips.length;
  }

  ngOnInit(): void {
    this.buildDatasets();
    this.resetDemo();
  }

  ngOnDestroy(): void {
    if (this.typingTimer) {
      window.clearInterval(this.typingTimer);
    }
    if (this.tipsTimer) {
      window.clearInterval(this.tipsTimer);
    }
  }

  setActiveCategory(index: number): void {
    this.activeCategoryIndex = index;
    this.activeLevelIndex = 0;
    this.activeLessonIndex = 0;
    this.resetDemo();
  }

  setActiveLevel(index: number): void {
    this.activeLevelIndex = index;
    this.activeLessonIndex = 0;
    this.resetDemo();
  }

  setActiveLesson(index: number): void {
    this.activeLessonIndex = index;
    this.resetDemo();
  }

  regenerateDataset(): void {
    const lesson = this.activeLesson;
    if (lesson.dataset) {
      this.datasets[lesson.id] = lesson.dataset;
      return;
    }
    this.datasets[lesson.id] = this.generateDataset(lesson.id);
  }

  revealTip(): void {
    if (!this.allTipsRevealed) {
      this.revealedTips += 1;
    }
  }

  submitSolution(): void {
    const attempt = this.userSolution.trim();
    if (!attempt || this.isSubmitting) {
      return;
    }

    const prompt = [
      'You are Gemini, a friendly coding tutor.',
      `Student level: ${this.skillLevel}. Passion level: ${this.passionLevel}/5.`,
      `Course: ${this.activeCategory.title} (${this.activeLevel.title}).`,
      `Lesson focus: ${this.activeLesson.title}.`,
      'Give tips and encouragement based on the student attempt without revealing the full answer right away.',
      'If they are stuck, escalate hints gradually but stay educational.',
      'Student attempt:',
      attempt
    ].join('\n');

    const request: CodeGemRequest = {
      prompt,
      level: this.skillLevel,
      passionLevel: this.passionLevel,
      patternId: this.activeLesson.id,
      userAttempt: attempt
    };

    this.messages.push({ role: 'user', content: attempt });
    this.userSolution = '';
    this.isSubmitting = true;

    this.codeGem
      .codeGem(request)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          this.messages.push({ role: 'assistant', content: response });
        },
        error: () => {
          this.messages.push({
            role: 'assistant',
            content: 'Gemini tutor is taking a quick coffee break. Please try again shortly.'
          });
        }
      });
  }

  convertMarkdown(markdown: string): string {
    return this.converter.makeHtml(markdown || '');
  }

  private resetDemo(): void {
    this.revealedTips = 1;
    this.startTyping();
    this.startTipCycle();
  }

  private startTyping(): void {
    if (this.typingTimer) {
      window.clearInterval(this.typingTimer);
    }
    const fullText = this.activeLesson.demoScript.join('\n');
    this.typedDemo = '';
    this.isTyping = true;
    let index = 0;
    this.typingTimer = window.setInterval(() => {
      this.typedDemo += fullText.charAt(index);
      index += 1;
      if (index >= fullText.length) {
        this.isTyping = false;
        if (this.typingTimer) {
          window.clearInterval(this.typingTimer);
        }
      }
    }, 18);
  }

  private startTipCycle(): void {
    if (this.tipsTimer) {
      window.clearInterval(this.tipsTimer);
    }
    this.tipsTimer = window.setInterval(() => {
      if (this.allTipsRevealed) {
        return;
      }
      this.revealedTips += 1;
    }, 12000);
  }

  private buildDatasets(): void {
    this.categories.forEach((category) => {
      category.levels.forEach((level) => {
        level.lessons.forEach((lesson) => {
          this.datasets[lesson.id] = lesson.dataset ?? this.generateDataset(lesson.id);
        });
      });
    });
  }

  private generateDataset(lessonId: string): string {
    const rand = (min: number, max: number): number =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    switch (lessonId) {
      case 'constant-transition': {
        const n = rand(6, 12);
        const houses = Array.from({ length: rand(5, 8) }, () => rand(1, 12));
        return `n = ${n}\nhouses = ${JSON.stringify(houses)}`;
      }
      case 'grid-pattern': {
        const rows = rand(3, 6);
        const cols = rand(3, 6);
        const obstacles = [
          [rand(1, rows - 1), rand(1, cols - 1)],
          [rand(1, rows - 1), rand(1, cols - 1)]
        ];
        return `grid = ${rows} x ${cols}\nobstacles = ${JSON.stringify(obstacles)}`;
      }
      case 'two-sequences': {
        const pool = 'dynamic';
        const makeWord = (len: number) =>
          Array.from({ length: len }, () => pool[rand(0, pool.length - 1)]).join('');
        return `a = "${makeWord(rand(5, 7))}"\nb = "${makeWord(rand(5, 7))}"`;
      }
      case 'interval-dp': {
        const pool = 'racecarlevelstats';
        const makeWord = (len: number) =>
          Array.from({ length: len }, () => pool[rand(0, pool.length - 1)]).join('');
        return `s = "${makeWord(rand(6, 9))}"`;
      }
      case 'non-constant': {
        const nums = Array.from({ length: rand(7, 10) }, () => rand(1, 30));
        return `nums = ${JSON.stringify(nums)}`;
      }
      case 'knapsack': {
        const nums = Array.from({ length: rand(5, 8) }, () => rand(1, 12));
        let total = nums.reduce((acc, val) => acc + val, 0);
        if (total % 2 !== 0) {
          nums[0] += 1;
          total = nums.reduce((acc, val) => acc + val, 0);
        }
        const target = total / 2;
        return `nums = ${JSON.stringify(nums)}\ntarget = ${target}`;
      }
      default:
        return 'dataset = []';
    }
  }
}